import {
	AfterViewInit,
	Component,
	ElementRef,
	EventEmitter,
	Input,
	NgZone,
	OnChanges,
	OnDestroy,
	Output,
	SimpleChanges,
	ViewChild
} from '@angular/core';
// Type-only imports: erased at compile time, so CodeMirror stays out of the main bundle.
import type { EditorView, ViewUpdate } from '@codemirror/view';
import type { HtmlEditorHandle } from './html-code-editor.codemirror';

const HTML_FILE_PATTERN = /\.html?$/i;

@Component({
	selector: 'app-html-code-editor',
	templateUrl: './html-code-editor.component.html',
	styleUrls: ['./html-code-editor.component.scss']
})
export class HtmlCodeEditorComponent implements AfterViewInit, OnChanges, OnDestroy {
	@Input() value: string = '';
	@Input() readOnly: boolean = false;
	@Input() minHeight: string = '600px';

	@Output() valueChange = new EventEmitter<string>();
	/** Full document text, emitted after a paste has been applied. */
	@Output() pasted = new EventEmitter<string>();
	/** Emitted after a dropped or picked .html/.htm file has replaced the document. */
	@Output() fileOpened = new EventEmitter<{ name: string; html: string }>();

	@ViewChild('editorHost', { static: true }) editorHost: ElementRef<HTMLDivElement>;
	@ViewChild('fileInput', { static: true }) fileInput: ElementRef<HTMLInputElement>;

	public kb: string = '0.0';
	public loading: boolean = true;
	/** Resolves once CodeMirror has loaded and the editor view exists. */
	public readonly ready: Promise<void>;

	private resolveReady: () => void;
	private handle: HtmlEditorHandle;
	private view: EditorView;
	private destroyed = false;
	/** True while the doc is being replaced from the `value` input, so no valueChange is emitted. */
	private applyingExternalValue = false;

	constructor(private zone: NgZone) {
		this.ready = new Promise<void>((resolve) => (this.resolveReady = resolve));
	}

	ngOnChanges(changes: SimpleChanges): void {
		if (changes['value']) {
			this.updateSize();
			if (this.view && (this.value ?? '') !== this.view.state.doc.toString()) {
				this.replaceDoc(this.value ?? '', false);
			}
		}
		if (this.handle && changes['readOnly']) {
			this.handle.setReadOnly(this.readOnly);
		}
		if (this.handle && changes['minHeight']) {
			this.handle.setMinHeight(this.minHeight);
		}
	}

	ngAfterViewInit(): void {
		import('./html-code-editor.codemirror')
			.then(({ createHtmlEditor }) => {
				if (this.destroyed) {
					return;
				}
				// Created from the latest `value`, so input changes made while loading are not lost.
				this.handle = createHtmlEditor({
					parent: this.editorHost.nativeElement,
					doc: this.value ?? '',
					readOnly: this.readOnly,
					minHeight: this.minHeight,
					onUpdate: (update) => this.onUpdate(update),
					onDragOver: (event) => this.onDragOver(event),
					onDrop: (event) => this.onDrop(event)
				});
				this.view = this.handle.view;
				this.zone.run(() => (this.loading = false));
				this.resolveReady();
			})
			.catch((error) => console.error('Failed to load the HTML code editor:', error));
	}

	ngOnDestroy(): void {
		this.destroyed = true;
		this.view?.destroy();
		this.view = null;
		this.handle = null;
	}

	/** Replaces the current selection with `text`, leaves the cursor after it, and focuses the editor. */
	public insertAtCursor(text: string): void {
		if (!this.view) {
			this.setValue((this.value ?? '') + text);
			return;
		}
		this.view.dispatch(this.view.state.replaceSelection(text), { scrollIntoView: true, userEvent: 'input' });
		this.view.focus();
	}

	public focus(): void {
		this.view?.focus();
	}

	public openFilePicker(): void {
		this.fileInput.nativeElement.click();
	}

	public onFileInputChange(event: Event): Promise<void> {
		const input = event.target as HTMLInputElement;
		const file = input.files?.[0];
		// Reset so picking the same file again still fires `change`.
		input.value = '';
		return file ? this.loadFile(file) : Promise.resolve();
	}

	private onDragOver(event: DragEvent): boolean {
		if (this.readOnly || !this.hasFiles(event)) {
			return false;
		}
		event.preventDefault();
		return true;
	}

	private onDrop(event: DragEvent): boolean {
		if (!this.hasFiles(event)) {
			return false; // Let CodeMirror handle ordinary text drags.
		}
		// Always swallow file drops so CodeMirror never inserts the raw bytes of a non-HTML file.
		event.preventDefault();
		if (!this.readOnly) {
			this.zone.run(() => this.loadFile(event.dataTransfer.files[0]));
		}
		return true;
	}

	private async loadFile(file: File): Promise<void> {
		if (!HTML_FILE_PATTERN.test(file.name)) {
			console.warn(`Ignored "${file.name}": only .html and .htm files can be opened.`);
			return;
		}
		const contents = await file.text();
		this.zone.run(() => {
			this.setValue(contents);
			this.fileOpened.emit({ name: file.name, html: contents });
		});
	}

	/** Replaces the whole document as a user action (emits valueChange). */
	private setValue(text: string): void {
		if (this.view) {
			this.replaceDoc(text, true);
		} else {
			this.value = text;
			this.updateSize();
			this.valueChange.emit(text);
		}
	}

	private replaceDoc(text: string, emit: boolean): void {
		this.applyingExternalValue = !emit;
		try {
			this.view.dispatch({
				changes: { from: 0, to: this.view.state.doc.length, insert: text },
				selection: { anchor: 0 }
			});
		} finally {
			this.applyingExternalValue = false;
		}
	}

	private onUpdate(update: ViewUpdate): void {
		if (!update.docChanged || this.applyingExternalValue) {
			return;
		}
		const text = update.state.doc.toString();
		const wasPaste = update.transactions.some((tr) => tr.isUserEvent('input.paste'));
		this.zone.run(() => {
			this.value = text;
			this.updateSize();
			this.valueChange.emit(text);
			if (wasPaste) {
				this.pasted.emit(text);
			}
		});
	}

	private updateSize(): void {
		const bytes = new TextEncoder().encode(this.value ?? '').length;
		this.kb = (bytes / 1024).toFixed(1);
	}

	private hasFiles(event: DragEvent): boolean {
		return !!event.dataTransfer && Array.from(event.dataTransfer.types ?? []).includes('Files');
	}
}
